@file:OptIn(ExperimentalWasmJsInterop::class)

package org.matthewo.table

import emotion.css.ClassName
import emotion.react.css
import js.objects.buildReadonlyRecord
import org.matthewo.api.EventCompetition
import org.matthewo.api.MetricsAPI
import org.matthewo.js.toFixed
import org.matthewo.pages.team.tabs.toTeam
import org.matthewo.router.contains
import org.matthewo.styles.*
import react.*
import react.dom.DOMAttributes
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.i
import react.dom.html.ReactHTML.span
import react.dom.html.ReactHTML.table
import react.dom.html.ReactHTML.tbody
import react.dom.html.ReactHTML.td
import react.dom.html.ReactHTML.thead
import react.dom.html.ReactHTML.tr
import tanstack.react.router.useNavigate
import tanstack.react.router.useSearch
import web.cssom.*
import web.html.HTMLTableCellElement
import kotlin.reflect.KProperty1

const val TEAM_SORT_SEARCH = "team"
const val STAT_SEARCH = "stat"
const val METRIC_SEARCH = "metric"

private val doubleRowCell = ClassName("double-row-cell")

private val recordsTable = ClassName {
    width = 100.vw

    "td" {
        padding = 0.5.em
    }

    doubleRowCell {
        padding = array(0.25.em, 0.5.em)
    }

    "thead td" {
        textAlign = TextAlign.center
    }

    "tr, td" {
        margin = 0.px
    }

    "tbody td" {
        textAlign = TextAlign.right
    }
}

private fun sortedIndicator(sorted: Boolean, order: Order) = when {
    sorted && order == Order.DESCENDING -> " ▼"
    sorted -> " ▲"
    else -> ""
}

fun <N : Number, M : MetricsAPI<N>> RecordsTable(
    year: Int,
    stats: List<Pair<String, KProperty1<EventCompetition<N, M?>, M?>>>,
    metrics: List<Pair<String, KProperty1<in M, N>>>,
    colors: List<ColorGroup>,
) = FC<RecordsTableProps<N, M>>("RecordsTable") { props ->
    val searchParams = useSearch()
    val navigate = useNavigate()

    val sorting = useMemo(searchParams) {
        val team = TEAM_SORT_SEARCH in searchParams
        val order = Order.parse(searchParams[ORDER_SEARCH].toString())
        if (team) RecordsTableSort.Team(order ?: Order.ASCENDING)
        else {
            val stat = stats.firstOrNull { it.second.name == searchParams[STAT_SEARCH] } ?: stats.first()
            val metric = metrics.firstOrNull { it.second.name == searchParams[METRIC_SEARCH] } ?: metrics.first()
            RecordsTableSort.StatMatric(stat.second, metric.second, order ?: Order.DESCENDING)
        }
    }

    val selectedColumn = useMemo(props.records) {
        if (props.records?.isEmpty() ?: true) return@useMemo null
        (sorting as? RecordsTableSort.StatMatric<*, *>)?.metric
    }

    useEffect(sorting) {
        if (sorting is RecordsTableSort.StatMatric<*, *>) {
            @Suppress("UNCHECKED_CAST")
            props.onSort?.invoke(sorting as RecordsTableSort.StatMatric<N, M>)
        }
    }

    context(attrs: DOMAttributes<*>)
    fun configureSortByTeamInteraction(): Boolean {
        val sorted = sorting is RecordsTableSort.Team
        if (props.sortableByTeam) {
            attrs.onClick = {
                navigate {
                    search = buildReadonlyRecord {
                        this[TEAM_SORT_SEARCH] = Unit
                        this[ORDER_SEARCH] = (if (sorted && sorting.order == Order.ASCENDING) Order.DESCENDING else Order.ASCENDING).value
                    }
                    replace = true
                }
            }
        }
        return sorted
    }

    val teams = useMemo(props.records, if (props.onSort == null) sorting else Unit) {
        when (sorting) {
            is RecordsTableSort.StatMatric<*, *> -> {
                @Suppress("UNCHECKED_CAST")
                sorting as RecordsTableSort.StatMatric<N, M>
                props.records?.sortedBy { sorting.stat(it)?.let { sorting.metric(it) }?.toInt() ?: if (sorting.order == Order.DESCENDING) Int.MIN_VALUE else Int.MAX_VALUE }
            }

            is RecordsTableSort.Team -> props.records?.sortedBy { it.number }
        }.let { if (sorting.order == Order.DESCENDING) it?.reversed() else it }
    }

    var displayTeams by useState(listOf<EventCompetition<N, M?>>())
    useEffect(teams) {
        if (null != teams) {
            displayTeams = teams
        }
    }

    div {
        css(recordsTable)

        table {
            css {
                fontSize = 1.rem
                borderCollapse = BorderCollapse.separate
                borderSpacing = 0.px
            }

            thead {
                css {
                    position = Position.sticky
                    top = 0.px
                    zIndex = integer(3)
                }
                tr {
                    css {
                        backgroundColor = BACKGROUND_COLOR
                    }
                    td {
                        css {
                            position = Position.sticky
                            left = 0.px
                            maxWidth = 2.5.em
                            minWidth = 2.5.em
                            backgroundColor = INFO_COLOR.tableHead
                            zIndex = integer(4)
                        }
                    }
                    td {
                        css {
                            position = Position.sticky
                            left = 3.5.em
                            backgroundColor = INFO_COLOR.tableHead
                            zIndex = integer(4)
                        }
                        val sorted = configureSortByTeamInteraction()
                        span {
                            css {
                                textDecoration = TextDecoration.underline
                            }
                            +"Team"
                        }
                        +sortedIndicator(sorted, sorting.order)
                    }
                    metrics.forEachIndexed { i, metric ->
                        td {
                            val rowRef = useRef<HTMLTableCellElement>()
                            ref = rowRef
                            useEffect(selectedColumn, rowRef) {
                                if (selectedColumn == metric.second) {
                                    rowRef.current?.scrollIntoView()
                                }
                            }
                            css {
                                backgroundColor = colors[i].tableHead
                            }
                            colSpan = stats.size
                            +metric.first
                        }
                    }
                }
                tr {
                    css {
                        backgroundColor = BACKGROUND_COLOR
                    }
                    td {
                        css {
                            position = Position.sticky
                            left = 0.px
                            backgroundColor = INFO_COLOR.tableHead
                            zIndex = integer(4)
                        }
                    }
                    td {
                        css {
                            position = Position.sticky
                            left = 3.5.em
                            backgroundColor = INFO_COLOR.tableHead
                            zIndex = integer(4)
                        }
                        configureSortByTeamInteraction()
                    }
                    metrics.forEachIndexed { i, metric ->
                        stats.forEach { stat ->
                            td {
                                css {
                                    backgroundColor = colors[i].tableHead
                                }
                                val s = stat.second
                                val m = metric.second
                                val sorted = sorting is RecordsTableSort.StatMatric<*, *> && sorting.stat == s && sorting.metric == m
                                onClick = {
                                    navigate {
                                        search = buildReadonlyRecord {
                                            this[STAT_SEARCH] = s.name
                                            this[METRIC_SEARCH] = m.name
                                            this[ORDER_SEARCH] = (if (sorted && sorting.order == Order.DESCENDING) Order.ASCENDING else Order.DESCENDING).value
                                        }
                                        replace = true
                                    }
                                }
                                span {
                                    css {
                                        textDecoration = TextDecoration.underline
                                    }
                                    +stat.first
                                }
                                +sortedIndicator(sorted, sorting.order)
                            }
                        }
                    }
                }
            }

            tbody {
                val teamNumbers = mutableSetOf<Int>()
                var rank = 0
                displayTeams.withIndex().forEach { (i, team) ->
                    val brightness = if (i % 2 == 1) 2.0 else 1.0

                    val bestPerformance = team.number !in teamNumbers
                    if (bestPerformance) {
                        rank++
                        teamNumbers.add(team.number)
                    }

                    tr {
                        css {
                            visibility = if (displayTeams != teams && null != props.onSort) Visibility.hidden else Visibility.visible
                        }

                        td {
                            css {
                                position = Position.sticky
                                left = 0.px
                                zIndex = integer(2)
                                backgroundColor = BACKGROUND_COLOR.lighten(brightness)
                                textWrap = TextWrap.nowrap
                            }
                            if (bestPerformance) +"${rank}. "
                        }

                        td {
                            css(doubleRowCell) {
                                position = Position.sticky
                                left = 3.5.em
                                zIndex = integer(2)
                                backgroundColor = BACKGROUND_COLOR.lighten(brightness)
                                borderRight = Border(1.px, LineStyle.solid, NamedColor.white)
                            }
                            onClick = {
                                navigate {
                                    toTeam(teamNumber = team.number, eventCode = props.event ?: team.event)
                                }
                            }
                            div {
                                css {
                                    textWrap = TextWrap.nowrap
                                    display = Display.flex
                                    flexDirection = FlexDirection.column
                                    textAlign = TextAlign.left
                                    maxWidth = min(20.em, 30.vw)
                                    if (true == props.showNew && team.new) color = NamedColor.yellow
                                }
                                span {
                                    css {
                                        overflow = Overflow.hidden
                                        textOverflow = TextOverflow.ellipsis
                                        textDecoration = TextDecoration.underline
                                    }
                                    +"${team.number}"
                                }
                                i {
                                    css {
                                        overflow = Overflow.hidden
                                        textOverflow = TextOverflow.ellipsis
                                    }
                                    +team.name
                                }
                            }
                        }

                        metrics.forEachIndexed { i, metric ->
                            stats.forEachIndexed { j, stat ->
                                td {
                                    css {
                                        border = Border(0.px, LineStyle.solid, NamedColor.white)
                                        if (i != 0 && j == 0) {
                                            borderLeftWidth = 0.5.px
                                        }
                                        if (j == stats.lastIndex) {
                                            borderRightWidth = 0.5.px
                                        }
                                        backgroundColor = colors[i].tableBody.lighten(brightness)
                                    }
                                    +(stat.second(team)?.let { metric.second(it) }?.toFixed(0) ?: "")
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

external interface RecordsTableProps<N : Number, M : MetricsAPI<N>> : Props {
    var records: List<EventCompetition<N, M?>>?
    var sortableByTeam: Boolean
    var onSort: ((RecordsTableSort.StatMatric<N, M>) -> Unit)?
    var event: String?
    var showNew: Boolean?
}

sealed class RecordsTableSort<M> {
    abstract val order: Order

    data class StatMatric<N : Number, M : MetricsAPI<N>>(
        val stat: KProperty1<EventCompetition<N, M?>, M?>,
        val metric: KProperty1<in M, N>,
        override val order: Order,
    ) : RecordsTableSort<M>()

    data class Team(override val order: Order) : RecordsTableSort<Nothing>()
}
